import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SettingsCard from '../../organisms/SettingsCard';
// import Button from '../../atoms/Button';
// import { Heading2 } from '../../atoms/Typography';
import Loading from '../../atoms/Loading';
import ToggleField from '../../molecules/ToggleField';
import Stepper from '../../organisms/Stepper';
import { SETTINGS_STEPS } from '../../../data/settingsSteps';
import { toUiFieldType } from '../../../data/formFieldPresets';
import { getFormFields } from '../../../redux/complaint/actions';
import { updateComplaintTypeActive } from '../../../redux/createComplaint/actions';
import Review from './Review';

const FIELD_TYPE_MAP = {
  CUSTOM: 'Custom',
  TEXT: 'Text',
  TEXTAREA: 'Text Area',
  SELECT: 'Select',
  DROPDOWN: 'Dropdown',
  NUMBER: 'Number',
  DATE: 'Date',
  DATETIME: 'Date Time',
  EMAIL: 'Email',
  // MULTISELECT: 'MultiSelect',
  // MULTI_SELECT: 'MultiSelect',
  CHECKBOX: 'Checkbox',
  RADIO: 'Radio',
  // MOBILE: 'Mobile',
  // PHONE: 'Mobile',
  FILE: 'File',
  IMAGES: 'Images',
  TIME: 'Time',
};

const FIELD_ACCESS_MAP = {
  BOTH: 'Both',
  USER: 'User',
  ADMIN: 'Admin',
};

function hideForLabel(step) {
  if (!step) return null;
  if (typeof step === 'string') return step;
  return step.name ?? step.label ?? step.title ?? null;
}

function uniqueValues(values) {
  return [...new Set(values.filter(Boolean))];
}

function optionLabel(value) {
  if (typeof value === 'string') return value;
  return value?.label ?? value?.name ?? '';
}

function toFieldKey(label) {
  const words = String(label || '')
    .trim()
    .split(/[^A-Za-z0-9]+/)
    .filter(Boolean);
  if (!words.length) return '';
  return (
    words[0].toLowerCase() +
    words
      .slice(1)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join('')
  );
}

function optionDependsOnValue(value) {
  if (!value || typeof value !== 'object') return '';
  return value.dependsOnValue ?? value.parent ?? '';
}

function resolveParentOptionLabel(dependsOnValue, parentValues = []) {
  if (!dependsOnValue) return '';
  const parentLabels = parentValues.map(optionLabel).filter(Boolean);
  const exact = parentLabels.find((label) => label === dependsOnValue);
  if (exact) return exact;
  const byKey = parentLabels.find((label) => (
    toFieldKey(label) === dependsOnValue || toFieldKey(label) === toFieldKey(dependsOnValue)
  ));
  return byKey || dependsOnValue;
}

function normalizeFormFields(list = []) {
  const sorted = [...list].sort(
    (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0),
  );

  return sorted.map((item) => {
    const values = item?.options?.values ?? [];
    const parent = sorted.find((field) => (
      field.id === item.dependsOnFieldId
      || (item.dependsOnFieldKey && field.fieldKey === item.dependsOnFieldKey)
    ));

    return {
      id: item.id,
      label: item.fieldName ?? item.fieldLabel ?? item.label ?? '',
      fieldLabel: item.fieldName ?? item.fieldLabel ?? item.label ?? '',
      fieldKey: item.fieldKey,
      fieldType: toUiFieldType(item.fieldType, FIELD_TYPE_MAP, item.fieldKey),
      required: Boolean(item.required),
      fieldAccess: FIELD_ACCESS_MAP[item.fieldAccess] ?? item.fieldAccess ?? '',
      hideFor: uniqueValues([
        ...(Array.isArray(item.hideFor) ? item.hideFor : []),
        hideForLabel(item.hideForWorkflowStep),
      ]),
      dependsOn: item.dependsOnField?.fieldName ?? parent?.fieldName ?? item.dependsOn ?? '',
      options: values.map(optionLabel).filter(Boolean),
      parentMappings: Object.fromEntries(
        values
          .map((value) => {
            const label = optionLabel(value);
            const mapped = optionDependsOnValue(value);
            if (!label || !mapped) return null;
            return [label, resolveParentOptionLabel(mapped, parent?.options?.values ?? [])];
          })
          .filter(Boolean),
      ),
    };
  });
}

export default function ReviewPublishStep({ onPublish: _onPublish, stepMeta, stepperProps, storeList }) {
  const dispatch = useDispatch();
  const complaintTypeId = useSelector((store) => store.appDetails?.id);
  const apiFields = useSelector((store) => store.complaintAppComplaint.formFields.data);
  const isLoading = useSelector((store) => store.complaintAppComplaint.formFields.isLoading);
  const error = useSelector((store) => store.complaintAppComplaint.formFields.error);
  const isActive = useSelector((store) => store.createComplaint?.isActive);
  const activeComplaintTypeId = useSelector((store) => store.createComplaint?.activeComplaintTypeId);
  const isUpdatingActive = useSelector((store) => store.createComplaint?.isUpdatingActive);
  const activeError = useSelector((store) => store.createComplaint?.activeError);
  const selectedType = useSelector((store) => store.complaintType?.selected);
  const complaintList = useSelector((store) => store.complaintType?.complaintList ?? []);
  const fields = useMemo(() => normalizeFormFields(apiFields), [apiFields]);
  const currentComplaintType = useMemo(() => {
    if (complaintTypeId == null) return null;
    if (selectedType && String(selectedType.id ?? selectedType._id) === String(complaintTypeId)) {
      return selectedType;
    }
    return complaintList.find((item) => String(item?.id ?? item?._id) === String(complaintTypeId)) ?? null;
  }, [complaintList, complaintTypeId, selectedType]);
  const persistedIsActive = currentComplaintType != null && (currentComplaintType.isActive != null || currentComplaintType.active != null)
    ? Boolean(currentComplaintType.isActive ?? currentComplaintType.active)
    : null;
  const isCurrentTypeActive =
    String(activeComplaintTypeId) === String(complaintTypeId)
      ? Boolean(isActive)
      : (persistedIsActive ?? true);

  useEffect(() => {
    if (!complaintTypeId) return undefined;
    dispatch(getFormFields(complaintTypeId));
  }, [complaintTypeId, dispatch]);

  const handleActiveToggle = (nextActive) => {
    if (!complaintTypeId || isUpdatingActive) return;
    dispatch(
      updateComplaintTypeActive({
        id: complaintTypeId,
        isActive: nextActive,
      }),
    );
  };

  return (
    <>
      <Stepper
        {...stepperProps}
        steps={SETTINGS_STEPS}
        showSaveButton={false}
        nextLabel="Publish"
        extraActions={(
          <ToggleField
            label="Active"
            checked={isCurrentTypeActive}
            disabled={!complaintTypeId || isUpdatingActive}
            onChange={handleActiveToggle}
          />
        )}
        onNext={() => {
          _onPublish?.();
          stepperProps.onNext?.();
        }}
      />
      {activeError ? (
        <div className="ca-validation-toast" role="alert">
          {activeError}
        </div>
      ) : null}
      <SettingsCard
      className="ca-settings-card--plain"
      title={stepMeta.title}
      description={stepMeta.description}
      showNext={false}
    >
      <div className="ca-review">
        <section className="ca-review__block ca-review__block--last">
          {/* <Heading2>Form Fields</Heading2> */}
          {isLoading ? (
            <Loading text="Loading form fields" />
          ) : error && !fields.length ? (
            <p className="ca-body">{error}</p>
          ) : (
            <Review fields={fields} storeList={storeList} />
          )}
        </section>

        {/* <div className="ca-workflow__actions">
          <Button onClick={_onPublish}>Publish</Button>
        </div> */}
      </div>
      </SettingsCard>
    </>
  );
}
